import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Play, Plus, RefreshCw, Trash2, Wifi } from "lucide-react";

import {
  type Source,
  type SourceCreate,
  type SourceTestResult,
  type SourceType,
  type SourceUpdate,
  SOURCE_TYPES,
  sourcesApi,
} from "@/api/sources";
import { useMe } from "@/hooks/useMe";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

function fmtDateTime(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("uk-UA", { timeZone: "Europe/Kyiv" });
}

export function SourcesPage() {
  const { t } = useTranslation();
  const me = useMe();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, SourceTestResult>>({});

  const isSuperAdmin = me.data?.role === "super_admin";

  const sources = useQuery({
    queryKey: ["sources"],
    queryFn: () => sourcesApi.list(),
    enabled: isSuperAdmin,
  });

  const remove = useMutation({
    mutationFn: (id: string) => sourcesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources"] }),
  });

  const testMut = useMutation({
    mutationFn: (id: string) => sourcesApi.test(id),
    onSuccess: (data, id) => setTestResults((p) => ({ ...p, [id]: data })),
    onError: (_e, id) =>
      setTestResults((p) => ({ ...p, [id]: { ok: false, message: "request failed" } })),
  });

  const runNow = useMutation({
    mutationFn: (id: string) => sourcesApi.runNow(id),
  });

  if (!isSuperAdmin) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500">{t("common.read_only")}</CardContent>
      </Card>
    );
  }

  const editing = sources.data?.find((s) => s.id === editingId) ?? null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("sources.title")}</CardTitle>
              <CardDescription>{t("sources.subtitle")}</CardDescription>
            </div>
            <Button onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" /> {t("sources.add")}
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {sources.isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : (sources.data ?? []).length === 0 ? (
            <div className="p-6 text-sm text-slate-500">{t("sources.no_sources")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="p-2">{t("sources.name")}</th>
                    <th className="p-2">{t("sources.type")}</th>
                    <th className="p-2">{t("sources.endpoint")}</th>
                    <th className="p-2">{t("sources.poll")}</th>
                    <th className="p-2">{t("sources.last_success")}</th>
                    <th className="p-2">{t("sources.failures")}</th>
                    <th className="p-2">{t("common.active")}</th>
                    <th className="p-2 text-right">{t("periods.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.data!.map((s) => {
                    const tr = testResults[s.id];
                    const endpoint = s.url ?? `${s.host ?? ""}${s.port ? ":" + s.port : ""}`;
                    return (
                      <tr key={s.id} className="border-t border-slate-100 align-top">
                        <td className="p-2">
                          <button
                            onClick={() => setEditingId(s.id)}
                            className="font-medium hover:underline text-left"
                          >
                            {s.name}
                          </button>
                          {s.has_password && (
                            <span className="ml-1 text-xs text-slate-400">🔒</span>
                          )}
                        </td>
                        <td className="p-2 font-mono text-xs">{s.type}</td>
                        <td className="p-2 text-xs text-slate-500">{endpoint || "—"}</td>
                        <td className="p-2">{s.poll_interval_min} {t("sources.min")}</td>
                        <td className="p-2 text-xs text-slate-500">{fmtDateTime(s.last_success_at)}</td>
                        <td
                          className={cn(
                            "p-2 font-mono",
                            s.consecutive_failures > 0 ? "text-red-700" : "text-slate-500",
                          )}
                        >
                          {s.consecutive_failures}
                        </td>
                        <td className="p-2">{s.is_active ? "✓" : "—"}</td>
                        <td className="p-2 text-right space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => testMut.mutate(s.id)}
                            disabled={testMut.isPending}
                            title={t("sources.test")}
                          >
                            <Wifi className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => runNow.mutate(s.id)}
                            disabled={runNow.isPending}
                            title={t("sources.run_now")}
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (confirm(t("sources.delete_confirm", { name: s.name }))) {
                                remove.mutate(s.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          {tr && (
                            <div
                              className={cn(
                                "mt-1 text-xs",
                                tr.ok ? "text-emerald-700" : "text-red-700",
                              )}
                            >
                              {tr.ok ? "✓" : "✗"} {tr.message}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {(adding || editing) && (
        <SourceFormModal
          source={editing}
          onClose={() => {
            setAdding(false);
            setEditingId(null);
          }}
          onSaved={() => {
            setAdding(false);
            setEditingId(null);
            void qc.invalidateQueries({ queryKey: ["sources"] });
          }}
        />
      )}
    </div>
  );
}

interface FormState {
  name: string;
  type: SourceType;
  url: string;
  host: string;
  port: string;
  login: string;
  password: string;
  poll_interval_min: number;
  retry_count: number;
  config_json: string;
  is_active: boolean;
}

function blankForm(): FormState {
  return {
    name: "",
    type: "web_cabinet_utp",
    url: "",
    host: "",
    port: "",
    login: "",
    password: "",
    poll_interval_min: 60,
    retry_count: 10,
    config_json: "{}",
    is_active: true,
  };
}

function fromSource(s: Source): FormState {
  return {
    name: s.name,
    type: s.type,
    url: s.url ?? "",
    host: s.host ?? "",
    port: s.port ? String(s.port) : "",
    login: s.login ?? "",
    password: "",
    poll_interval_min: s.poll_interval_min,
    retry_count: s.retry_count,
    config_json: JSON.stringify(s.config ?? {}, null, 2),
    is_active: s.is_active,
  };
}

function SourceFormModal({
  source,
  onClose,
  onSaved,
}: {
  source: Source | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(source ? fromSource(source) : blankForm());
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      let configParsed: Record<string, unknown> | null = null;
      try {
        const parsed = JSON.parse(form.config_json || "{}");
        configParsed = parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        throw new Error("invalid_config_json");
      }
      const body: SourceCreate = {
        name: form.name,
        type: form.type,
        url: form.url || null,
        host: form.host || null,
        port: form.port ? Number(form.port) : null,
        login: form.login || null,
        password: form.password || null,
        poll_interval_min: form.poll_interval_min,
        retry_count: form.retry_count,
        config: configParsed,
        is_active: form.is_active,
      };
      if (source) {
        // Update: don't send `type` (immutable in backend) and only include password if non-empty.
        const patch: SourceUpdate = { ...body };
        delete (patch as { type?: SourceType }).type;
        if (!form.password) delete patch.password;
        return sourcesApi.update(source.id, patch);
      }
      return sourcesApi.create(body);
    },
    onSuccess: () => onSaved(),
    onError: (e: unknown) => {
      if (e instanceof Error && e.message === "invalid_config_json") {
        setError(t("sources.errors.invalid_config_json"));
        return;
      }
      const code = (e as AxiosError<{ error?: { code?: string } }>)?.response?.data?.error?.code;
      setError(code ?? t("clients.errors.unknown"));
    },
  });

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>{source ? t("sources.edit") : t("sources.add")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t("sources.name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>{t("sources.type")}</Label>
              <select
                disabled={!!source}
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as SourceType })}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100"
              >
                {SOURCE_TYPES.map((tp) => (
                  <option key={tp} value={tp}>
                    {tp}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>URL</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Host</Label>
                <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Port</Label>
                <Input
                  type="number"
                  value={form.port}
                  onChange={(e) => setForm({ ...form, port: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Login</Label>
              <Input value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>{t("sources.password")}</Label>
              <Input
                type="password"
                placeholder={source ? t("sources.password_keep_hint") : ""}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>{t("sources.poll")}, {t("sources.min")}</Label>
              <Input
                type="number"
                min={1}
                value={form.poll_interval_min}
                onChange={(e) => setForm({ ...form, poll_interval_min: Number(e.target.value) })}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("sources.retry_count")}</Label>
              <Input
                type="number"
                min={1}
                value={form.retry_count}
                onChange={(e) => setForm({ ...form, retry_count: Number(e.target.value) })}
              />
            </div>
            <div className="space-y-1 flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                {t("common.active")}
              </label>
            </div>
          </div>

          <div className="space-y-1">
            <Label>config (JSON)</Label>
            <textarea
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-mono min-h-[120px]"
              value={form.config_json}
              onChange={(e) => setForm({ ...form, config_json: e.target.value })}
              spellCheck={false}
            />
            <p className="text-xs text-slate-500">{t("sources.config_hint")}</p>
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
        </CardContent>
        <div className="flex gap-2 px-6 pb-6">
          <Button onClick={() => save.mutate()} disabled={save.isPending || !form.name}>
            <RefreshCw className={cn("h-4 w-4", save.isPending && "animate-spin")} />
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
